import React, { useState, useEffect, useRef } from 'react';
import './SearchableSelect.css';

const SearchableSelect = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Selecione...", 
  displayField, 
  valueField = '_id',
  required = false,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Get display text for an option
  const getDisplayText = (option) => {
    if (!option) return '';
    
    if (typeof displayField === 'function') {
      return displayField(option);
    }
    
    if (typeof displayField === 'string') {
      return option[displayField];
    }
    
    // For cliente, try common fields
    return option.nomeFantasia || option.razaoSocial || option.nome || '';
  };

  // Get selected option
  const selectedOption = options.find(opt => opt[valueField] === value);
  const displayValue = selectedOption ? getDisplayText(selectedOption) : '';

  // Update search term when value changes (for edit mode)
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [value, isOpen]);

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => {
        const text = getDisplayText(option).toLowerCase();
        return text.includes(searchTerm.toLowerCase());
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, displayField]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option[valueField]);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen && inputRef.current) {
        setTimeout(() => inputRef.current.focus(), 0);
      }
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  return (
    <div className="searchable-select" ref={dropdownRef}>
      <div 
        className={`select-input ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleInputClick}
      >
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Digite para buscar..."
            disabled={disabled}
          />
        ) : (
          <span className={`select-value ${!displayValue ? 'placeholder' : ''}`}>
            {displayValue || placeholder}
          </span>
        )}
        
        <div className="select-actions">
          {value && !isOpen && (
            <button 
              type="button"
              className="clear-btn"
              onClick={handleClear}
              disabled={disabled}
            >
              ×
            </button>
          )}
          <span className={`arrow ${isOpen ? 'up' : 'down'}`}>▼</span>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="select-dropdown">
          {filteredOptions.length === 0 ? (
            <div className="no-options">Nenhuma opção encontrada</div>
          ) : (
            <ul className="options-list">
              {filteredOptions.map((option) => (
                <li
                  key={option[valueField]}
                  className={`option ${option[valueField] === value ? 'selected' : ''}`}
                  onClick={() => handleSelect(option)}
                >
                  {getDisplayText(option)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      
      {/* Hidden input for form validation */}
      {required && (
        <input
          type="text"
          value={value || ''}
          required
          style={{ position: 'absolute', left: '-9999px' }}
          onChange={() => {}}
        />
      )}
    </div>
  );
};

export default SearchableSelect;
